import Page from "design/pages/Page";
import React from "react";
import PageContainer from "design/layouts/PageContainer";
import WorkflowToolbar from "components/toolbars/WorkflowToolbar";
import MyTasksTable from "design/components/tables/MytasksTable";
import { EscalateInfo, ICardElementWithChildren } from "backend/model/model";
import MyTasksContainer from "design/pages/MyTasksContainer";
import useMyTasks from "backend/hooks/workflows/useMyTasks";
import MyTasksMenuBtn from "design/components/popups/MyTasksMenuBtn";
import DelegateModal from "design/components/popups/DelegateModal";
import EscalateModal from "design/components/popups/EscalateModal";
import useLink from "@qpr/client-api-base/hooks/useLink";
import useDelegateUsers from "backend/hooks/workflows/useDelegateUsers";
import Todo from "design/components/misc/Todo";
import DelegateModalPopup from "components/workflow/DelegateModalPopup";
import { t } from "i18next";
import PeriodChip from "design/components/chips/PeriodChip";
import EscalateModalPopup from "components/workflow/EscalateModalPopup";
import Alert from "@material-ui/lab/Alert";
import GenericModal from "design/components/popups/GenericModal";
import useCurrentUser from "@qpr/client-api-base/context/UserContext";
import SettingsBackupRestoreIcon from "@material-ui/icons/SettingsBackupRestore";
import { Button } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import FlagIcon from "@material-ui/icons/Flag";
import { formatWFStatus, getWFStatusBadgeStyle } from "utils/utils";

const MyTasks = () => {
  const theme = useTheme();
  const link = useLink();
  const { data: myTasks, isLoading: isMyTasksLoading } = useMyTasks("coming");
  const [openDelegateModal, setopenDelegateModal] = React.useState<
    ICardElementWithChildren | undefined
  >(undefined);
  const [openEscalateModal, setopenEscalateModal] = React.useState<
    ICardElementWithChildren | undefined
  >(undefined);
  const [openEscalateInfo, setOpenEscalateInfo] = React.useState<
    EscalateInfo | undefined
  >();
  const user = useCurrentUser();

  const sortedMyTasks = React.useMemo(() => {
    const list = [...(myTasks || [])].sort((a, b) => {
      if (a.workflowState.escalateInfo && !b.workflowState.escalateInfo)
        return -1;
      if (!a.workflowState.escalateInfo && b.workflowState.escalateInfo)
        return 1;

      if (a.workflowState.delegateInfo && !b.workflowState.delegateInfo)
        return -1;
      if (!a.workflowState.delegateInfo && b.workflowState.delegateInfo)
        return 1;

      if (
        a.workflowState.responsible === user.fullname &&
        b.workflowState.responsible !== user.fullname
      )
        return -1;

      if (
        b.workflowState.responsible === user.fullname &&
        a.workflowState.responsible !== user.fullname
      )
        return 1;

      return (
        (b.scorecardname || "").localeCompare(a.scorecardname || "") ||
        a.name.localeCompare(b.name)
      );
    });
    return list;
  }, [myTasks]);

  const selectElement = React.useCallback(
    (item: ICardElementWithChildren) => {
      console.log("selected ", item);
      link({
        page: "workflowinput",
        meaId: item.workflowState?.wfInstanceId,
      });
    },
    [myTasks]
  );

  return (
    <Page isLoading={isMyTasksLoading || !myTasks} Toolbar={WorkflowToolbar}>
      <EscalateModalPopup
        wfState={openEscalateModal?.workflowState}
        onClose={() => {
          setopenEscalateModal(undefined);
        }}
        isOpen={!!openEscalateModal}
        elem={openEscalateModal}
      />
      <DelegateModalPopup
        onClose={() => {
          setopenDelegateModal(undefined);
        }}
        isOpen={!!openDelegateModal}
        elem={openDelegateModal}
      />
      <GenericModal
        open={!!openEscalateInfo}
        onClose={() => {
          setOpenEscalateInfo(undefined);
        }}
      >
        <div>{openEscalateInfo?.message}</div>
        <div>
          <i>{openEscalateInfo?.senderFullname}</i>
        </div>
      </GenericModal>
      <MyTasksContainer>
        <PageContainer>
          <MyTasksTable
            items={sortedMyTasks || []}
            columns={[
              {
                // tdClassName: styles.workflowStatus,
                title: "Period",
                sortFn: (a, b) =>
                  a.workflowState?.period?.localeCompare(
                    b.workflowState?.period || "undefined"
                  ) || -1,
                onRowClick: selectElement,
                value: ({ elem }) => (
                  <div>
                    <PeriodChip period={elem.workflowState?.period!} />
                  </div>
                ),
              },
              {
                // tdClassName: styles.workflowStatus,
                title: t("mytasks.Workflow Status"),
                sortFn: (a, b) =>
                  formatWFStatus(a.workflowState?.state).localeCompare(
                    formatWFStatus(b.workflowState?.state)
                  ) ||
                  (a.workflowState?.state || "").localeCompare(
                    b.workflowState?.state || "undefined"
                  ) ||
                  -1,
                onRowClick: selectElement,
                value: ({ elem }) => {
                  const workflowState = elem.workflowState?.state;
                  const statusLabel = formatWFStatus(workflowState);
                  const badgeStyle = getWFStatusBadgeStyle(workflowState, theme);

                  return (
                    <div>
                      <div
                        onClick={(e) => {
                          if (elem.workflowState?.escalateInfo) {
                            setOpenEscalateInfo(elem.workflowState?.escalateInfo);
                            e.preventDefault();
                            e.stopPropagation();
                          }
                        }}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          flexWrap: "wrap",
                        }}
                      >
                        <div
                          style={{
                            ...badgeStyle,
                            display: "inline-flex",
                            alignItems: "center",
                            minHeight: "28px",
                            padding: "0 12px",
                            borderRadius: "999px",
                            fontSize: "12px",
                            fontWeight: 600,
                            lineHeight: 1,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {statusLabel}
                        </div>

                        {elem.workflowState?.delegateInfo ? (
                          <div
                            title={elem.workflowState.delegateInfo.message}
                            style={{
                              background: "#3377ff",
                              color: "white",
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              width: "28px",
                              height: "28px",
                              borderRadius: "999px",
                            }}
                          >
                            <FlagIcon style={{ fontSize: "16px" }} />
                          </div>
                        ) : (
                          ""
                        )}
                      </div>
                    </div>
                  );
                },
              },
              {
                onRowClick: selectElement,
                title: () => t("mytasks.Name"),
                value: ({ elem }) => <div>{elem.workflowState?.wftype}</div>,
                /*
                  <div className={styles.nameCellContainer}>
                    <div className={styles.pill}>
                      {elem.workflowState?.wftype}
                    </div>
                  </div>                
                */
              },
              {
                onRowClick: selectElement,
                title: t("mytasks.Entity"),
                value: ({ elem }) => (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                    }}
                  >
                    <div>{elem.name ?? "-"}</div>
                    <div>
                      <i>{elem.scorecardname}</i>
                    </div>
                  </div>
                ),
              },
              {
                onRowClick: selectElement,
                title: t("mytasks.Received From"),
                value: ({ elem }) => (
                  <div>{elem.workflowState?.receivedfrom ?? "-"}</div>
                ),
              },
              {
                onRowClick: selectElement,
                title: t("mytasks.Responsible"),
                value: ({ elem }) => (
                  <div>{elem.workflowState?.responsible ?? "-"}</div>
                ),
              },
              {
                onRowClick: selectElement,
                title: t("mytasks.Request Date"),
                value: ({ elem }) => (
                  <div>
                    {elem.workflowState && elem.workflowState.startdate
                      ? new Date(
                          elem.workflowState!.startdate
                        ).toLocaleDateString()
                      : "-"}
                  </div>
                ),
              },
              {
                onRowClick: selectElement,
                title: t("mytasks.Due Date"),
                value: ({ elem }) => (
                  <div>
                    {elem.workflowState && elem.workflowState.duedate
                      ? new Date(
                          elem.workflowState!.duedate
                        ).toLocaleDateString()
                      : "-"}
                  </div>
                ),
              },

              {
                title: t("mytasks.Actions"),
                value: ({ elem }) => (
                  <MyTasksMenuBtn
                    items={[
                      { name: "delegate", title: "Delegate" },
                      ...(elem.workflowState?.responsible !== user.fullname
                        ? [{ name: "escalate", title: "Escalate" }]
                        : []),
                    ]}
                    onSelected={(item) => {
                      if (item.name === "delegate") {
                        setopenDelegateModal(elem);
                      }
                      if (item.name === "escalate") {
                        setopenEscalateModal(elem);
                      }
                    }}
                  />
                ),
              },
            ]}
          />
        </PageContainer>
      </MyTasksContainer>
    </Page>
  );
};

export default MyTasks;
